// React imports
import React from 'react';
import classnames from 'classnames';
import { Provider } from 'react-redux';

// UI Imports
import {
  AmplifyCore,
  caseUtils,
  CodeGradingSections,
  commonEnums,
  activity,
  loadStates,
  launchPageActions,
  appServiceLines
} from '@codametrix/ui-common';
import {
  Card,
  LoadingSlugs,
  Modal,
  UniversalFeedback
} from '@codametrix/ui-components';

import * as dataURIs from '.././case-detail/_data-uris';

// Components imports
import { objectUtils } from '@codametrix/ui-common';
import SpinnerTag from '../../assets/spinner/spinner.svg';
import VitalWareModal from './code-bench.vw-modal';

import { BillingGroup } from '../billing-group/billing-group';
import { CodeBenchPanelv2 } from '../code-bench-panel/code-bench-panelv2';
import { CaseDetailv2 } from '../case-detail/case-detailv2';
import { CodeBenchActionBarv2 } from '../code-bench-action-bar/code-bench-action-barv2';
import { ProcessStatus } from '../process-status/process-status';
import { ChargeCounts } from '../code-bench-panel/charge-counts/charge-counts';
import { Timeline } from '../timeline/timeline';
import { Confirm } from '../confirm/confirm';
import { ChargeSelector } from '../charge-selector/charge-selector';
import { Improvements } from '../improvements/improvements';
import { CMXIcon } from '@codametrix/ui-components';
import { RelatedCases } from '../related-cases/related-cases';
import { StatusDescription } from '../case-status/case-status';
import { StandalonePageableList } from '../standalone-pageable-list/standalone-pageable-list';
import { Conditional } from '@codametrix/ui-components';
import { CaseAlert } from '../case-alert/case-alert';
import { launchAmplifyCase } from '../case-detail/window-open-args';
import GradeBenchPanelGeneralized from '../code-bench-panel/grade-bench-panel-generalized';
import { InvoiceMetadataFormFieldsCQA } from '../code-bench-panel/invoice-metadata/invoice-metadata-form-fixtures';
import { PurposeAndGuideline } from './purpose-guideline/purpose-guideline';
import {
  createSubmitCaseHandler,
  createSubmitCQAWorkItemHandler
} from '../code-bench-action-bar/code-bench-action-bar-component-utilities';
import { ItemSelectProps } from '../code-bench-action-bar/item-selector/item-select';
import {
  getSelectedWorkitemFromInvoice,
  GroupWorkitemsByStatus,
  isWorkitemSelected,
  renderWorkitemInItemSelector,
  renderWorkItemLabel
} from '../code-bench-action-bar/item-selector/workitem-selector';
import { hasLedgerUpdates } from '../../utils/utils';

/**
 * Temporary kill switch: set to `true` to show the reference-codes ledger update
 * banner when `hasLedgerUpdates` is satisfied.
 */
const ENABLE_REFERENCE_CODES_LEDGER_UPDATE_BANNER = false;

const { CaseAlertType, AccelerateProcessStatus } = commonEnums;

const { getChildWindowParams } = caseUtils;

enum TitleTabsEnum {
  CHANGES_TEXT = 'See changes to this case'
}

const { conditionalUtils } = AmplifyCore;

interface UpdatePropertyConfig {
  serviceLine: string;
  benchMode: Accelerate.BenchMode;
  propertyConfig: CMxCommonApp.PatientInformationConfiguration;
}

type CodeBenchState = {
  showCaseConflictModal: boolean;
  conflictingUser: string;
  versionsSeenCount: number;
};

/**
 * Place to update, transform, or modify property config.
 *
 * This function can help to separate the the React view from the React state before
 * sharing the object with PatientEncounterv2
 *
 * @param UpdatePropertyConfig
 * @returns An updated PatientInformationConfiguration
 */
function updatePropertyConfig({
  serviceLine,
  benchMode,
  propertyConfig
}: UpdatePropertyConfig): CMxCommonApp.PatientInformationConfiguration {
  const _serviceLine = serviceLine ? serviceLine.toLocaleLowerCase() : '';

  // For cqa workflow, serviceLine comes from activeItem which is set in code-bench.reducer.ts when loadCQAItemFromURL.
  // This service line is specifically based on the cqa-service workitem. Product requirement is to display
  // exam_description in CQA grading screen only for radiology.
  if (benchMode === commonEnums.BenchMode.GRADE) {
    return {
      ...propertyConfig,
      summary: propertyConfig.summary.filter((summary) => {
        if (
          summary.key === 'exam_description' &&
          !['radiology'].includes(_serviceLine)
        ) {
          return false;
        }

        return true;
      })
    };
  }

  return { ...propertyConfig };
}

class CodeBenchv2 extends React.Component<
  Accelerate.CodeBenchPanelProps,
  CodeBenchState
> {
  caseDetailRef: React.RefObject<CaseDetailv2>;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  timerId: NodeJS.Timeout | null = null;
  timerStarted = false;

  static defaultProps = {
    suggestedCodes: {
      cpt: [],
      modifier: [],
      diagnosis: []
    },
    benchEditable: { ...AmplifyCore.state.DEFAULT_BENCH_EDITABLE },
    improvements: { ...AmplifyCore.state.improvements },
    capabilities: { ...AmplifyCore.state.capabilities },
    relatedCasesLoadState: { ...loadStates.LOAD_STATES.done },
    relatedCasesPageable: {
      ...AmplifyCore.paginationUtils.relatedCasesSortablePageable
    },
    processInstanceGuids: [],
    hostCaseId: null,
    loadAlwaysFromURL: false,
    propertyConfig: {
      patient: [],
      summary: [],
      expanded: []
    },
    confirm: {
      display: false,
      title: ''
    },
    activity: [],
    noteTypes: [],
    workItem: {
      assignee: '',
      owner: '',
      aging_days: '',
      codable_days: ''
    },
    onLoad: (caseRun: CMxAPI.CaseRun) => {},
    onChargeSessionAction: (
      transactionAction: CMxCommonApp.TransactionAction
    ) => {},
    onReload: (params: any) => {},
    onSearchClear: (codeQuery: Accelerate.CodeActionContextual) => {},
    onCodeSearch: (codeQuery: Accelerate.CodeQuery) => {},
    onCodeSearchSelection: (
      searchSelection: Accelerate.CodeAddActionPayload
    ) => {},
    handleAddAllCodes: (searchSelection: Accelerate.CodesAddCodeBench) => {},
    handleRemoveAllCodes: (codeChange: Accelerate.CodeChange) => {},
    createTransactionAndAddCode: (
      searchSelection: Accelerate.CodeAddActionPayload
    ) => {},
    onChargeSessionMetaEdit: (chargeSession: CMxCommonApp.ChargeSession) => {},
    onCodeUpdate: (changeDetail: Accelerate.CodeChange) => {},
    onCodeRemove: (changeDetail: Accelerate.DeleteCQACodeParams) => {},
    onTransactionOrderChange: (
      transactionReorderDetail: Accelerate.TransactionReorderDetail
    ) => {},
    onTimeBasedCodeAdd: (time: string, age: string) => {},
    onTimeBasedCodeUpdate: (time: string, age: string) => {},
    onCodeSearchDrop: (changeDetail: Accelerate.CodeChange) => {},
    onTransactionSharedDiagnosisChange: (
      changeDetail: CMxCommonApp.TransactionSharedCodeChange
    ) => {},
    onPrompt: (action: string) => {},
    onPopUpClose: () => {},
    onFeatureActivation: (selection: CMxCommonApp.ChargeSessionSelection) => {},
    onNavigate: (caseId: string, replace?: boolean, serviceLine?: string) => {},
    hidevitalwareIssuesModal: () => {},
    onImprovementAdd: (
      documentImprovement: AmplifyAPI.DocumentImprovement
    ) => {},
    onImprovementUpdate: (
      documentImprovement: AmplifyAPI.DocumentImprovement
    ) => {},
    onImprovementRemove: (
      documentImprovement: AmplifyAPI.DocumentImprovement
    ) => {},
    onProviderAdd: (documentImprovement: AmplifyAPI.DocumentImprovement) => {},
    onProviderRemove: (
      documentImprovement: AmplifyAPI.DocumentImprovement
    ) => {},
    onProviderUpdate: (
      documentImprovement: AmplifyAPI.DocumentImprovement
    ) => {},
    onDocumentImprovementSearch: (codeQuery: CMxCommonApp.CodeQuery) => {},
    showAmplifyFeedback: (feedback: AmplifyAPI.AmplifyFeedback) => {},
    onProviderSearch: (query: CMxCommonApp.LookupQuery) => {},
    updateValue: (val: any) => {},
    updateCase: (idAndUpdates: any) => {},
    loadRoles: (roles: string[]) => {},
    getFlagItems: (activeItem: CMxAPI.CaseRunV2) => {},
    setActiveMiniTab: (activeTabName: string) => {},
    setActiveVerticalMiniTab: (activeTabName: string) => {},
    setUnresolvedDocuments: (value: boolean) => {},
    showCoderNotesInfo: (coderNotesParams: CMxCommonApp.CoderNotesParams) => {},
    onInvoiceRemove: (
      invoiceDeleteParams: Accelerate.InvoiceDeleteParams
    ) => {},
    searchResults: {},
    codeSearchResultsV2: {},
    displayAddCharge: false,
    showAddChargeError: false,
    showLoadingSubmission: false,
    showLoadingCancelation: false,
    showSizeExpWarning: false,
    showFeedBack: false,
    showSubmittedFeedback: false,
    showCanceledFeedback: false,
    submittedFeedbackTimer: false,
    showvitalwareIssuesModal: false,
    toggleHoldCaseModal: false,
    toggleExclusionCaseModal: false,
    showLateCaseClearModal: false,
    toggleSplitCaseModal: false,
    submitOldCaseModal: false,
    vitalwareIssues: [],
    mergeSplitHistoryIssues: [],
    caseAccessTime: '',
    activeMiniTab: commonEnums.MiniTabLabels.CHARGES_LABEL,
    activeVerticalMiniTab: commonEnums.MiniTabLabels.NOTES_LABEL,
    showUnresolvedDocuments: false,
    gradeableInvoices: [],
    qaRuleCollection: {
      sampleReason: '',
      guideline: ''
    },
    associatedWorkitems: []
  };

  constructor(props: Accelerate.CodeBenchPanelProps) {
    super(props);
    this.onCodeUpdate = this.onCodeUpdate.bind(this);
    this.onCodeRemove = this.onCodeRemove.bind(this);
    this.onTransactionOrderChange = this.onTransactionOrderChange.bind(this);
    this.onTimeBasedCodeAdd = this.onTimeBasedCodeAdd.bind(this);
    this.onTimeBasedCodeUpdate = this.onTimeBasedCodeUpdate.bind(this);
    this.onCodeSearchDrop = this.onCodeSearchDrop.bind(this);
    this.onCodeSearch = this.onCodeSearch.bind(this);
    this.onCodeSearchSelection = this.onCodeSearchSelection.bind(this);
    this.onAddAllCodes = this.onAddAllCodes.bind(this);
    this.onRemoveAllCodes = this.onRemoveAllCodes.bind(this);
    this.createTransactionAndAddCode =
      this.createTransactionAndAddCode.bind(this);
    this.onFeatureSelection = this.onFeatureSelection.bind(this);
    this.handleGroupedCaseClick = this.handleGroupedCaseClick.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleInvoiceEdit = this.handleInvoiceEdit.bind(this);
    this.handlePromptDismiss = this.handlePromptDismiss.bind(this);
    this.handleMetaAction = this.handleMetaAction.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onModalAddChargeOpen = this.onModalAddChargeOpen.bind(this);
    this.onModalAddChargeClose = this.onModalAddChargeClose.bind(this);
    this.handleAddCharge = this.handleAddCharge.bind(this);
    this.handleTimer = this.handleTimer.bind(this);
    this.handleInvoiceAddition = this.handleInvoiceAddition.bind(this);
    this.handleTransactionSharedDiagnosisChange =
      this.handleTransactionSharedDiagnosisChange.bind(this);
    this.handleDeleteSizeExpWarning =
      this.handleDeleteSizeExpWarning.bind(this);
    this.handleEnableTechPro = this.handleEnableTechPro.bind(this);
    this.handleDisableTechPro = this.handleDisableTechPro.bind(this);

    this.caseDetailRef = React.createRef();
    this.iframeRef = React.createRef();

    const codeUpdateDebounced = this.onCodeUpdate.bind(this);
    this.onCodeUpdate = objectUtils.debounce(codeUpdateDebounced, 500);
    this.handleHARCaseList = this.handleHARCaseList.bind(this);
    this.state = {
      showCaseConflictModal: false,
      conflictingUser: '',
      versionsSeenCount: 1
    };
  }

  componentDidUpdate(
    prevProps: Readonly<Accelerate.CodeBenchPanelProps>,
    prevState: Readonly<any>,
    snapshot?: any
  ): void {
    if (!this.props.loadAlwaysFromURL) return;
    // --- ACQUIRE CASE ACCESS ---
    const case_uid = this.props.match?.params?.caseId ?? this.props.match?.params?.id;
    const becameEditable =
      this.props.benchEditable?.editable && !prevProps.benchEditable?.editable;

    if (becameEditable && !this.timerStarted) {
      this.props.acquireCaseAccess({
        case_uid,
        processInstance: this.props.processInstance,
        callback: result => {
          if (result?.conflict) {
            const fullName = result?.openedByName
              ? `${result.openedByName}`
              : 'N/A';
            this.setState({
              showCaseConflictModal: true,
              conflictingUser: fullName
            });
            return;
          }
          if (result?.success) {
            this.startTimer(case_uid);
            this.timerStarted = true;
          }
        }
      });
    }
    // --- RELEASE CASE ACCESS ---
    const becameNonEditable =
      prevProps.benchEditable?.editable && !this.props.benchEditable?.editable;
    if (becameNonEditable && this.timerStarted) {
      this.props.releaseCaseAccess(case_uid);
      this.clearTimer();
      this.props.resetCaseAccess(this.props.processInstance);
    }

    if (prevProps.match?.url !== this.props.match?.url) {
      this.props.match.params.getCaserunWithRefresh =
        conditionalUtils.capabilityValue(
          this.props?.capabilities?.capabilities?.getCaseRunWithRefresh?.on ??
            false
        );
      this.props.onLoadFromURLMatch(this.props.match);
      const case_uid = this.props.match?.params?.caseId;
      const caserun_uid = this.props.match?.params?.id;
      this.props.loadWorkItemDetails(case_uid, caserun_uid);
      // Loads cursorAPIContext from connected props(from dashboard-ui) to capabilities state(shared-views)
      this.props.loadServicedeskContext(this.props.cursorAPIContext);
      if (this.props.benchMode == commonEnums.BenchMode.GRADE) {
        this.props.fetchGradePanelConfigurations();
        if (this.props.cursorAPIContext.qaCollectionUuid) {
          this.props.fetchQaRule({
            qaCollectionUuid: this.props.cursorAPIContext.qaCollectionUuid,
            level: 3,
            service: this.props.cursorAPIContext.launchConfigServiceLine
          });
        }
        this.props.fetchWorkerInfoForUser(this.props.userContext);
      }
    }

    if (this.props.benchMode == commonEnums.BenchMode.GRADE) {
      if (prevProps.gradeableInvoices !== this.props.gradeableInvoices) {
        this.props.fetchGraderWorkItemActivity(this.props.match.params.id);
      }
    }

    // New compare group: reset version counter only (no auto-navigation to first associated item)
    const prevGroup = prevProps.associatedWorkitems?.[0]?.data?.case_id;
    const currGroup = this.props.associatedWorkitems?.[0]?.data?.case_id;
    const isNewGroup =
      currGroup != null &&
      prevGroup !== currGroup &&
      this.props.associatedWorkitems.length > 1;

    if (isNewGroup) {
      this.setState({ versionsSeenCount: 1 });
    }
    if (prevProps.userContext !== this.props.userContext) {
      const serviceLine = appServiceLines.isIPC(
        this.props?.match?.params?.serviceLine
      )
        ? 'Inpatient Professional Coding'
        : this.props?.match?.params?.serviceLine;
      this.props.fetchCoderConfig({
        section: commonEnums.ConfigurationSection.CODER_CONFIG,
        serviceLine,
        //@ts-ignore TODO: active tenantId appears to be mapped to userContext
        tenantId: this.props.userContext?.tenantId
      });
    }
  }

  componentDidMount() {
    this.props.loadRoles(this.props.roles);
    const hasActiveItem = !objectUtils.isEmpty(this.props.activeItem);
    let case_uid = '',
      caserun_uid = '';
    if (this.props.loadAlwaysFromURL || !hasActiveItem) {
      this.props.match.params.getCaserunWithRefresh =
        conditionalUtils.capabilityValue(
          this.props?.capabilities?.capabilities?.getCaseRunWithRefresh?.on ??
            false
        );
      this.props.onLoadFromURLMatch(this.props.match);
      case_uid = this.props.match?.params?.caseId;
      caserun_uid = this.props.match?.params?.id;
    } else {
      this.props.onLoad(this.props.activeItem);
      case_uid = this.props.activeItem.case_uid;
      caserun_uid = this.props.activeItem.caserun_uid;
    }

    this.props.loadWorkItemDetails(case_uid, caserun_uid);

    // Loads cursorAPIContext from connected props(from dashboard-ui) to capabilities state(shared-views)
    this.props.loadServicedeskContext(this.props.cursorAPIContext);

    if (this.props.benchMode == commonEnums.BenchMode.GRADE) {
      this.props.fetchGradePanelConfigurations();
      if (this.props.cursorAPIContext.qaCollectionUuid) {
        this.props.fetchQaRule({
          qaCollectionUuid: this.props.cursorAPIContext.qaCollectionUuid,
          level: 3,
          service: this.props.cursorAPIContext.launchConfigServiceLine
        });
      }
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.cleanupCaseAccess();
  }

  handleBeforeUnload = () => {
    this.cleanupCaseAccess();
  };

  cleanupCaseAccess = () => {
    const hasActiveItem = !objectUtils.isEmpty(this.props.activeItem);
    let case_uid = '';
    if (this.props.loadAlwaysFromURL || !hasActiveItem) {
      case_uid = this.props.match?.params?.caseId ?? this.props.match?.params?.id;
    } else {
      case_uid = this.props.activeItem.case_uid;
    }
    if (this.timerStarted) {
      this.props.releaseCaseAccess(case_uid);
    }
    this.clearTimer();
    this.props.resetCaseAccess(this.props.processInstance);
  };

  startTimer = caseUid => {
    // avoid duplicate timers
    this.clearTimer();
    this.timerId = setInterval(() => {
      this.props.refreshCaseAccessTimer(caseUid);
    }, 300000);
  };

  clearTimer() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
      this.timerStarted = false;
    }
  }

  /**
   * Helper function to release current case access, clear timer, and reset case access state
   */
  handleNextCompareVersion = (cursorAPIPayload: CMxCommonApp.CursorAPIPayload) => {
    const { associatedWorkitems, loadAssociatedWorkItem } = this.props;
    const currentId = cursorAPIPayload.cursorAPIContext.workitemId;
    const currentIndex = associatedWorkitems.findIndex(
      (wi: CMxAPI.WorkItem) => wi.data.case_id === currentId
    );
    const length = associatedWorkitems.length;
    const nextIndex = (currentIndex + 1) % length;
    const nextWorkitem = associatedWorkitems[nextIndex];
    if (nextWorkitem) {
      loadAssociatedWorkItem(cursorAPIPayload.cursorAPIContext, nextWorkitem);
      this.setState(prev => ({
        versionsSeenCount: prev.versionsSeenCount + 1
      }));
    }
  };

  releaseCurrentCaseAccess = () => {
    // reset state
    this.setState({
      showCaseConflictModal: false,
      conflictingUser: ''
    });
    if (this.timerStarted && (this.props.processInstance || this.props.benchMode === commonEnums.BenchMode.GRADE)) {
      const case_uid = this.props.activeItem?.case_uid ?? this.props.match?.params?.id;
      if (case_uid) {
        this.props.releaseCaseAccess(case_uid);
        this.clearTimer();
        this.props.resetCaseAccess(this.props.processInstance);
      }
    }
  };

  handleCancel(event: React.MouseEvent) {
    event.preventDefault();
    this.props.onInvoicesCancel(this.props.processInstance);
  }

  handleSubmit(
    event: React.MouseEvent,
    vwComment: string = null,
    submitWithOverrideButton: boolean = false,
    remove_hold: boolean = null
  ) {
    event.preventDefault();

    const cursorAPIPayload: CMxCommonApp.CursorAPIPayload = {
      operation: commonEnums.CursorOperation.NEXT,
      cursorAPIContext: this.props.cursorAPIContext,
      hasAmplifyCaseRunV2Access: conditionalUtils.capabilityValue(
        this.props.capabilities.capabilities.amplifyCaseRunV2?.on ?? false
      )
    };

    this.props.onInvoicesSubmisson({
      processInstance: this.props.processInstance,
      uiMode: this.props.UIMode,
      vwComment,
      submitWithOverrideButton,
      cursorAPIPayload,
      activeItem: this.props.activeItem as CMxAPI.CaseRunV2,
      removeHold: remove_hold
    });
  }

  handlePreviousClick(event: React.MouseEvent) {
    event.preventDefault();
    const { processInstanceGuids, activeItem } = this.props;

    const loc = processInstanceGuids.findIndex(
      (item) => item === activeItem.process_instance_guid
    );
    this.props.handleTraversal(processInstanceGuids[loc - 1]);
  }

  handleModalClose(ev: React.MouseEvent) {
    ev.preventDefault();
    this.props.onPopUpClose();
  }

  handlePromptDismiss(ev: React.MouseEvent) {
    ev.preventDefault();
    this.props.onPrompt('REFRESH_TOKEN');
  }

  handleConfirm(actionItem: Accelerate.ActionItem) {
    this.props.onPopUpClose();
  }

  handleConfirmDismiss(actionItem: Accelerate.ActionItem) {
    this.props.onPopUpClose();
  }

  handleInvoiceEdit(invoice: CMxCommonApp.CodableInvoice) {
    this.props.onInvoiceEdit(invoice, this.props.processInstance);
  }

  handleInvoiceAddition(invoices: CMxCommonApp.CodableInvoice[]) {
    this.props.onInvoiceAddition(
      (invoices && invoices.length > 0
        ? invoices[0]
        : null) as CMxCommonApp.CodableInvoice | null,
      this.props.processInstance
    );
  }

  handleMetaAction(transactionAction: CMxCommonApp.TransactionAction) {
    const invoice = this.props.codableInvoices.find(
      (invoice) => invoice.guid === transactionAction.meta.invoice.guid
    );
    this.props.onChargeSessionAction({
      ...transactionAction,
      meta: {
        ...transactionAction.meta,
        invoice: invoice,
        processInstance: this.props.processInstance
      }
    });
  }

  handleGroupedCaseClick(ev: React.MouseEvent) {
    ev.preventDefault();
    const target =
      (ev.target as HTMLDivElement) || document.createElement('div');
    const linkable = target?.closest('a');
    if (!linkable) return;

    const item = linkable.attributes.getNamedItem('data-case-id');
    if (!item) return;

    const caseId = item.value;
    if (!caseId) return;

    const hostCaseId = this.props.hostCaseId
      ? this.props.hostCaseId
      : this.props.activeItem.case_id;

    const replace = hostCaseId !== this.props.activeItem.case_id;

    this.props.onPopUpNavigate({
      caseId: item.value,
      replace,
      serviceLine: this.props.activeItem.service_line,
      hostCaseId
    });
  }

  onFeatureSelection(selection: CMxCommonApp.ChargeSessionSelection) {
    this.props.onFeatureActivation(selection);
    this.caseDetailRef.current.onFeatureActivation(selection);
  }
  // The CodeQuery, SearchResult, and CodeChange all rely on a common
  // property called context.  This is what will allow business
  // logic upstream to have standard patterns for figuring out what to do
  // with various actions.

  onCodeSearch(commonCodeQuery: CMxCommonApp.CodeQuery) {
    const context = {
      ...commonCodeQuery.context,
      activeItem: { ...this.props.activeItem },
      userContext: { ...this.props.userContext }
    };
    const codeQuery = { ...commonCodeQuery, context };
    const isMod = codeQuery.searchType === commonEnums.CodeType.MODIFIER;
    if (isMod) {
      codeQuery.text = `${codeQuery.text}`.toUpperCase();
    }
    //
    if (codeQuery.text.length > 2 || (codeQuery.text.length === 2 && isMod)) {
      this.props.onCodeSearch(codeQuery);
    }
  }

  onCodeSearchSelection(commonSearchResult: Accelerate.CodeAddActionPayload) {
    if (objectUtils.isEmpty(commonSearchResult)) {
      return;
    }
    const defaultPrevented =
      commonSearchResult.context?.defaultPrevented ?? false;

    if (defaultPrevented) {
      this.props.onSearchClear({
        context: {
          ...commonSearchResult.context,
          activeItem: this.props.activeItem
        }
      });
    } else {
      const context = {
        ...commonSearchResult.context,
        activeItem: { ...this.props.activeItem },
        invoices: [...this.props.codableInvoices]
      };
      const searchResult: Accelerate.CodeAddActionPayload = {
        codeInfo: commonSearchResult.codeInfo,
        additionalCodes: commonSearchResult.additionalCodes,
        context
      };
      this.props.onCodeSearchSelection(searchResult);
    }
  }

  onAddAllCodes(commonSearchResult: Accelerate.CodesAddCodeBench) {
    if (objectUtils.isEmpty(commonSearchResult?.codesInfo)) {
      return;
    }
    const defaultPrevented =
      commonSearchResult.context?.defaultPrevented ?? false;

    if (defaultPrevented) {
      this.props.onSearchClear({
        context: {
          ...commonSearchResult.context,
          activeItem: this.props.activeItem
        }
      });
    } else {
      const context = {
        ...commonSearchResult.context,
        activeItem: { ...this.props.activeItem },
        invoices: [...this.props.codableInvoices]
      };
      const searchResult: Accelerate.CodesAddCodeBench = {
        codesInfo: commonSearchResult.codesInfo,
        context
      };
      this.props.handleAddAllCodes(searchResult);
    }
  }

  createTransactionAndAddCode(
    commonSearchResult: Accelerate.CodeAddActionPayload
  ) {
    if (objectUtils.isEmpty(commonSearchResult)) {
      return;
    }
    const context = {
      ...commonSearchResult.context,
      activeItem: { ...this.props.activeItem },
      invoices: [...this.props.codableInvoices]
    };
    const searchResult: Accelerate.CodeAddActionPayload = {
      codeInfo: commonSearchResult.codeInfo,
      additionalCodes: commonSearchResult.additionalCodes,
      context
    };
    this.props.createTransactionAndAddCode(searchResult);
  }

  onCodeUpdate(commonChangeDetail: CMxCommonApp.CodeChange) {
    const context = {
      ...commonChangeDetail.context,
      activeItem: { ...this.props.activeItem },
      invoices: [...this.props.codableInvoices]
    };
    const changeDetail = { ...commonChangeDetail, context };

    this.props.onCodeUpdate(changeDetail);
  }

  onCodeRemove(commonChangeDetail: CMxCommonApp.CodeChange) {
    const context = {
      ...commonChangeDetail.context,
      activeItem: { ...this.props.activeItem },
      invoices: [...this.props.codableInvoices]
    };
    const changeDetail = { ...commonChangeDetail, context };
    this.props.onCodeRemove(changeDetail);
  }

  onRemoveAllCodes(commonChangeDetail: CMxCommonApp.CodeChange) {
    if (objectUtils.isEmpty(commonChangeDetail?.codes)) {
      return;
    }
    const context = {
      ...commonChangeDetail.context,
      activeItem: { ...this.props.activeItem },
      invoices: [...this.props.codableInvoices]
    };
    const changeDetail = { ...commonChangeDetail, context };
    this.props.handleRemoveAllCodes(changeDetail);
  }

  onTimeBasedCodeAdd(
    commonChangeDetail: CMxCommonApp.CodeChange,
    time: string,
    age: string
  ) {
    const context = {
      ...commonChangeDetail.context,
      activeItem: { ...this.props.activeItem },
      invoices: [...this.props.codableInvoices]
    };
    const codeChange = { ...commonChangeDetail, context };
    this.props.onTimeBasedCodeAdd(codeChange, time, age);
  }
  onTimeBasedCodeUpdate(
    commonChangeDetail: CMxCommonApp.CodeChange,
    time: string,
    age: string
  ) {
    const context = {
      ...commonChangeDetail.context,
      activeItem: { ...this.props.activeItem },
      invoices: [...this.props.codableInvoices]
    };
    const codeChange = { ...commonChangeDetail, context };
    this.props.onTimeBasedCodeUpdate(codeChange, time, age);
  }
  onTransactionOrderChange(
    transactionReorderDetail: CMxCommonApp.TransactionReorderDetail
  ) {
    this.props.onTransactionOrderChange(transactionReorderDetail);
  }
  onCodeSearchDrop(commonChangeDetail: CMxCommonApp.CodeChange) {
    const context = {
      ...commonChangeDetail.context,
      activeItem: { ...this.props.activeItem },
      invoices: [...this.props.codableInvoices]
    };
    const changeDetail = { ...commonChangeDetail, context };
    this.props.onCodeSearchDrop(changeDetail);
  }

  handleTransactionSharedDiagnosisChange(
    changeDetail: CMxCommonApp.TransactionSharedCodeChange
  ) {
    this.props.onTransactionSharedDiagnosisChange(changeDetail);
  }

  handleHARCaseList(options?: CMxCommonApp.SortablePageable<any>) {
    this.props.getCasesWithEncounter({
      encounterId: this.props.activeItem?.encounter_identifier?.number,
      sortablePageable: options,
      serviceLine: this.props.activeItem?.service_line,
      filterSelection: this.props.relatedCasesFilterSelection
    });
  }

  /**
   * Event that happen when click add charge button
   * @returns {void}
   */
  onModalAddChargeOpen() {
    this.props.onModalAddChargeOpen();
  }

  /**
   * Event that happen when Enter key is pressed on  add charge button
   * @returns {void}
   */

  handleKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.onModalAddChargeOpen();
    }
  };

  /**
   * Event that happen when moda of add charge button is closed
   * @returns {void}
   */
  onModalAddChargeClose(e) {
    this.props.onModalAddChargeClose(e);
  }

  /**
   * Event launched when invoice is clicked
   */
  handleAddCharge(invoice: AmplifyAPI.Invoice) {
    if (invoice.deleted) {
      this.handleInvoiceAddition([invoice] as CMxCommonApp.CodableInvoice[]);
    } else {
      this.props.handleAddCharge({
        invoiceGuid: invoice.guid,
        processInstanceId: this.props.processInstance.guid.uuid,
        activeItem: this.props.activeItem
      });
    }
  }

  /**
   * Method that is called for decrement timer
   */
  handleTimer() {
    this.props.handleTimer();
  }

  /**
   * Method that is called when user wants to close the modal for size experience warning
   */
  handleDeleteSizeExpWarning() {
    this.props.deleteSizeExpWarning();
  }

  /**
   * Method used for enable a procedure with tech/pro charge
   * @param context
   */
  handleEnableTechPro(
    techCodableTransaction: CMxCommonApp.TechCodableTransaction
  ) {
    techCodableTransaction.processInstance = this.props.processInstance;
    this.props.handleEnableTechPro(techCodableTransaction);
  }

  /**
   * Method used for disable a procedure with tech/pro charge
   * @param context
   */
  handleDisableTechPro(
    techCodableTransaction: CMxCommonApp.TechCodableTransaction
  ) {
    techCodableTransaction.processInstance = this.props.processInstance;
    this.props.handleDisableTechPro(techCodableTransaction);
  }
  onRelatedCasesFilterApply = (filters: CMxCommonApp.FilterSelection) => {
    this.props.updateRelatedCasesFilterSelection(filters);
    this.props.getCasesWithEncounter({
      encounterId: this.props.activeItem?.encounter_identifier?.number,
      sortablePageable: {
        ...AmplifyCore.paginationUtils.relatedCasesSortablePageable
      },
      serviceLine: this.props.activeItem?.service_line,
      filterSelection: filters
    });
  };

  render() {
    const {
      nonCodeBench,
      chargeSessions,
      counts,
      searchResults,
      codeSearchResultsV2,
      benchEditable,
      processInstance,
      activations,
      groupedCases,
      modalURL,
      hostCaseId,
      onChargeSessionAction,
      activity,
      prompt,
      confirm,
      showSubmittedFeedback,
      showLoadingSubmission,
      showLoadingCancelation,
      showSizeExpWarning,
      showCanceledFeedback,
      submittedFeedbackTimer,
      showvitalwareIssuesModal,
      toggleHoldCaseModal,
      toggleExclusionCaseModal,
      showLateCaseClearModal,
      toggleSplitCaseModal,
      vitalwareIssues,
      mergeSplitHistoryIssues,
      documentImprovementIssues,
      holdIssues,
      hidevitalwareIssuesModal,
      displayAddCharge,
      showAddChargeError,
      showAddChargeLoading,
      loadState,
      codableInvoices,
      feedback,
      showFeedBack,
      UIMode,
      epicToken,
      codeEdits,
      invoiceOptions,
      caseEdits,
      showHoldCaseModal,
      hideHoldCaseModal,
      showExclusionCaseModal,
      hideExclusionCaseModal,
      toggleLateCaseClearModal,
      clearLateCaseNotification,
      toggleChargeableCase,
      holdCase,
      improvements,
      capabilities,
      updateValue,
      updateCase,
      relatedCases,
      relatedCasesPageable,
      flagList,
      htmlNotes,
      customClaimsRelatedCases,
      customClaimsRelatedCasesLoadState,
      organization,
      benchMode,
      caseVersions,
      gradeableInvoices,
      fieldDefinitions: fieldDefinitionsFromProps,
      riskCategories,
      cqaCodeAssessmentRiskDictionaries,
      onGradeUpdate,
      onGradeRemove,
      onCodeEvent,
      onCreateCQATransaction,
      onDeleteCQATransaction,
      workItem,
      suggestedCodes,
      associatedWorkitems,
      loadAssociatedWorkItem,
      ...caseDetailProps
    } = this.props;

    const fieldDefinitions = {
      cptFields:
        fieldDefinitionsFromProps?.cptFields ?? CodeGradingSections.CPTCodeSection,
      icdFields:
        fieldDefinitionsFromProps?.icdFields ?? CodeGradingSections.ICDCodeSection,
      modifierFields:
        fieldDefinitionsFromProps?.modifierFields ??
        CodeGradingSections.ModifierCodeSection
    };

    function handleOpenCase(processInstanceGuid: string, caseUid?: string) {
      if (processInstanceGuid) {
        const childWindowParams = getChildWindowParams(
          caseUid ?? caseDetailProps.activeItem?.case_uid,
          processInstanceGuid,
          caseDetailProps.activeItem?.service_line,
          launchAmplifyCase,
          caseDetailProps.activeItem?.case_id
        );
        caseDetailProps.openChildWindow(childWindowParams);
      }
    }
    const currentVersionPosition =
      caseVersions?.findIndex(
        (caseVersion) =>
          caseVersion.processInstanceGuid === processInstance?.guid?.uuid
      ) ?? -1;

    const previousVersions = caseVersions?.filter(
      (_, index) => index < currentVersionPosition
    );

    const previousSentCase = previousVersions?.find(
      (previousVersion) =>
        previousVersion.processInstanceStatus ===
          AccelerateProcessStatus.SUBMITTED ||
        previousVersion.processInstanceStatus ===
          AccelerateProcessStatus.CANCELLED ||
        previousVersion.processInstanceStatus === AccelerateProcessStatus.SENT
    );

    const newestVersionObject: AmplifyAPI.CaseVersion =
      caseVersions?.length > 0 ? caseVersions.slice().reverse().find(
        (caseVersion) => caseVersion.processInstanceStatus?.indexOf('REPLACED') < 0
      ) : null;

    const flagListSet: string[] = Array.from(
      new Set(flagList?.map((flag) => flag?.flag))
    );

    let submitHandler = null;

    if (benchMode == commonEnums.BenchMode.GRADE) {
      let updatedStatus = 'CQA_COMPLETE';

      if (
        gradeableInvoices.length > 0 &&
        gradeableInvoices[0].status == 'CQA_COMPLETE'
      ) {
        updatedStatus = 'CQA_APPROVED';
      }

      submitHandler = createSubmitCQAWorkItemHandler(
        this.props.cursorAPIContext,
        this.props.submitCQAWorkitem,
        updatedStatus,
        this.releaseCurrentCaseAccess
      );
    } else {
      submitHandler = createSubmitCaseHandler(
        UIMode,
        this.props.onInvoicesSubmisson,
        this.props.processInstance,
        this.props.cursorAPIContext,
        this.props.caseAccessTime,
        this.props.activeItem,
        this.props.getCaseVersions,
        this.props.toggleSubmitOldCaseModal,
        conditionalUtils.capabilityValue(
          capabilities.capabilities.amplifyCaseRunV2?.on ?? false
        ),
        this.releaseCurrentCaseAccess
      );
    }

    const actionBarSelectMultipleWorkitems = conditionalUtils.capabilityValue(
      this.props.capabilities?.capabilities?.actionBarSelectMultipleWorkitems
        ?.on
    );

    let onCursorActionHandler = this.props.onCursorAction;

    if (
      benchMode == commonEnums.BenchMode.GRADE &&
      actionBarSelectMultipleWorkitems
    ) {
      onCursorActionHandler = (
        cursorAPIPayload: CMxCommonApp.CursorAPIPayload
      ) => {
        if (associatedWorkitems && associatedWorkitems.length > 0) {
          const wlGroupingId = associatedWorkitems[0].data.wl_grouping_id;
          cursorAPIPayload.cursorAPIContext = {
            ...cursorAPIPayload.cursorAPIContext,
            wl_grouping_id: wlGroupingId ?? cursorAPIPayload.cursorAPIContext.wl_grouping_id
          };
          this.props.onCursorAction(cursorAPIPayload);
        }
      };
    } else {
      // Wrap the original onCursorAction to release case access before navigation
      onCursorActionHandler = (
        cursorAPIPayload: CMxCommonApp.CursorAPIPayload
      ) => {
        // Release current case access before navigating to next case
        this.releaseCurrentCaseAccess();

        // Call the original onCursorAction
        this.props.onCursorAction(cursorAPIPayload);
      };
    }

    const [selectedWorkitem] =
      getSelectedWorkitemFromInvoice(
        this.props.cursorAPIContext,
        associatedWorkitems
      );
    const groupedWorkitems = GroupWorkitemsByStatus(associatedWorkitems);
    const nextVersionButtonConfig = {
      text: 'Next Version',
      tooltip: 'Navigate to next version of the work item',
      enabled:
        actionBarSelectMultipleWorkitems &&
        associatedWorkitems.length > 1 &&
        this.state.versionsSeenCount < associatedWorkitems.length
    };
    let workitemSelectorProps: ItemSelectProps<CMxAPI.WorkItem> = {
      items: groupedWorkitems,
      selectedItem: selectedWorkitem,
      getItemLabel: (item: CMxAPI.WorkItem) => renderWorkItemLabel(item),
      onItemSelect: (item: CMxAPI.WorkItem) =>
        loadAssociatedWorkItem(this.props.cursorAPIContext, item),
      renderItem: (item: CMxAPI.WorkItem) => renderWorkitemInItemSelector(item),
      buttonLabel: selectedWorkitem?.data?.assignee
        ? `${selectedWorkitem?.data?.assignee}`
        : 'Select Workitem',
      prefixTitle: '',
      className: 'action-bar-item-selector',
      disabled: false,
      disableWhenSingle: false,
      isSelected: isWorkitemSelected,
      secondaryLabel: `${this.state.versionsSeenCount} of ${associatedWorkitems.length}`,
      isGrouped: true
    };

    const actionBar = (
      //@ts-ignore
      <CodeBenchActionBarv2
        {...this.props}
        handleSubmitCase={submitHandler}
        itemSelectorProps={
          actionBarSelectMultipleWorkitems && associatedWorkitems.length > 0
            ? workitemSelectorProps
            : undefined
        }
        nextVersionButtonConfig={nextVersionButtonConfig}
        onCursorAction={onCursorActionHandler}
        onNextVersion={this.handleNextCompareVersion}
        conflictingUser={this.state.conflictingUser}
        caseAccessState={this.props.caseAccessState}
      />
    );

    let codeEditingPanel;
    let button, relatedCasesView;

    let isActive = !caseUtils.isFinishedCase(caseDetailProps.activeItem);

    if (showSubmittedFeedback || showCanceledFeedback) {
      if (submittedFeedbackTimer) {
        window.parent.postMessage(
          {
            action: 'Epic.Clinical.Informatics.Web.CloseActivity',
            token: epicToken
          },
          '*'
        );
      } else {
        this.handleTimer();
      }
    }

    const showProcessStatus = !benchEditable.editable;
    const showModal = modalURL !== null && modalURL.path;
    const modalURI = showModal ? modalURL.path + modalURL.queryString : '';
    const benchLoading = loadState.loading;
    const invoices = invoiceOptions?.invoices ?? [];
    const hasStatusReason = caseUtils.getStatusReason(
      caseDetailProps.activeItem
    );

    const styles = classnames([
      'code-benchv2',
      {
        'bench-pre-editable': benchEditable.waiting,
        'bench-cancelled': benchEditable.cancelled,
        'bench-loading': benchLoading
      }
    ]);

    if (benchEditable.waiting) {
      codeEditingPanel = <ProcessStatus benchEditable={benchEditable} />;
    } else {
      const noCodableInvoices =
        codableInvoices === null || codableInvoices.length === 0;

      const noGradeableInvoice =
        gradeableInvoices == null || gradeableInvoices.length === 0;

      const noCharges = noCodableInvoices && noGradeableInvoice;

      const summarySection = (
        <div>
          <div key="charge-totals" className="summary-section">
            <h5>Summary</h5>
            <ChargeCounts counts={counts} />
          </div>
        </div>
      );

      const panel =
        benchMode == commonEnums.BenchMode.CODE ? (
          <CodeBenchPanelv2
            key="code-bench-panel"
            panelActivations={activations.panel}
            onChargeSessionAction={this.handleMetaAction}
            onFeatureSelection={this.onFeatureSelection}
            editable={benchEditable.editable}
            benchEditable={benchEditable}
            caseDetailProps={caseDetailProps}
            searchResults={searchResults}
            codeSearchResultsV2={codeSearchResultsV2}
            benchLoading={benchLoading}
            codeEdits={codeEdits}
            onInvoiceMetaEdit={this.handleInvoiceEdit}
            onCodeSearchSelection={this.onCodeSearchSelection}
            handleAddAllCodes={this.onAddAllCodes}
            handleRemoveAllCodes={this.onRemoveAllCodes}
            createTransactionAndAddCode={this.createTransactionAndAddCode}
            onCodeSearch={this.onCodeSearch}
            onCodeUpdate={this.onCodeUpdate}
            onCodeRemove={this.onCodeRemove}
            onTimeBasedCodeAdd={this.onTimeBasedCodeAdd}
            onTimeBasedCodeUpdate={this.onTimeBasedCodeUpdate}
            onCodeSearchDrop={this.onCodeSearchDrop}
            onTransactionSharedDiagnosisChange={
              this.handleTransactionSharedDiagnosisChange
            }
            counts={counts}
            processInstance={processInstance}
            codableInvoices={codableInvoices}
            showProcessStatus={showProcessStatus}
            handleEnableTechPro={this.handleEnableTechPro}
            handleDisableTechPro={this.handleDisableTechPro}
            customClaimsRelatedCases={this.props.customClaimsRelatedCases}
            customClaimsRelatedCasesLoadState={
              this.props.customClaimsRelatedCasesLoadState
            }
            openChildWindow={this.props.openChildWindow}
            handleInvoiceAddition={() =>
              this.handleInvoiceAddition(
                invoices as CMxCommonApp.CodableInvoice[]
              )
            }
            onInvoiceRemove={this.props.onInvoiceRemove}
            summarySection={summarySection}
            transactionV2={conditionalUtils.capabilityValue(
              capabilities.capabilities.transactionV2?.on
            )}
            suggestedCodes={suggestedCodes}
            onTransactionOrderChange={this.onTransactionOrderChange}
          />
        ) : (
          <GradeBenchPanelGeneralized
            key="grade-bench-panel"
            invoiceMetadataFields={InvoiceMetadataFormFieldsCQA}
            cptFields={fieldDefinitions.cptFields}
            icdFields={fieldDefinitions.icdFields}
            modifierFields={fieldDefinitions.modifierFields}
            gradeableInvoices={gradeableInvoices}
            onCodeUpdate={onGradeUpdate}
            onCodeRemove={onGradeRemove}
            onCodeEvent={onCodeEvent}
            codeSearch={{
              onCodeSearch: this.onCodeSearch,
              //Code search selection and drop are disabled for now, until gradebench gets its own codeUpdate actions
              onCodeSearchSelection: this.onCodeSearchSelection,
              onCodeSearchDrop: () => {},
              searchResults
            }}
            onCreateCQATransaction={onCreateCQATransaction}
            onDeleteCQATransaction={onDeleteCQATransaction}
            projectConfiguration={
              this.props.qaRuleCollection.project_configuration
            }
            riskCategories={riskCategories}
            cqaCodeAssessmentRiskDictionaries={
              cqaCodeAssessmentRiskDictionaries
            }
            gradingErrors={this.props.gradingErrors}
            resolverValidationErrors={this.props.resolverValidationErrors ?? null}
            editable={benchEditable?.editable}
          />
        );
      const innerBench =
        (noCodableInvoices && codableInvoices === null) || loadState.loading ? (
          <LoadingSlugs key="loading-slugs" numberItems={3} />
        ) : (
          [
            noCharges ? (
              <div className="no-charges empty-message">No charges found.</div>
            ) : (
              panel
            )
          ]
        );

      if (AmplifyCore.isIPCServiceline(this.props.activeItem.service_line)) {
        relatedCasesView = (
          <>
            <StandalonePageableList
              list={this.handleHARCaseList}
              sortablePageable={relatedCasesPageable}
              theme={'cmx-theme'}>
              <RelatedCases
                relatedCases={relatedCases}
                showLoading={
                  benchLoading || caseDetailProps.relatedCasesLoadState.loading
                }
                activeItem={this.props.activeItem}
                onOpenRelatedCase={this.props.openChildWindow}
                showProviderInfo={this.props.showProviderInfo}
                filterData={this.props.filterData}
                relatedCasesFilterSelection={
                  this.props.relatedCasesFilterSelection
                }
                onRelatedCasesFilterApply={this.onRelatedCasesFilterApply}
                relatedCasesFilterCapability={
                  capabilities.capabilities.relatedCasesFilter
                }
              />
            </StandalonePageableList>
          </>
        );
      } else {
        relatedCasesView = (
          <>
            {benchLoading || caseDetailProps.billingGroupLoadState.loading ? (
              <LoadingSlugs key="loading-slugs" numberItems={1} />
            ) : (
              <div
                className="status-section"
                onClick={this.handleGroupedCaseClick}>
                <BillingGroup
                  suppressedLinks={[hostCaseId]}
                  showProcessStatus={true}
                  isActive={isActive}
                  cancelled={benchEditable.cancelled}
                  groupedCases={groupedCases}
                  showLoading={caseDetailProps.billingGroupLoadState.loading}
                  viewOnly={!benchEditable.editable}
                />
              </div>
            )}
          </>
        );
      }

      codeEditingPanel = [
        <>
          {/* Automation to manual status reason */}
          {benchMode == commonEnums.BenchMode.GRADE ? (
            <PurposeAndGuideline
              purpose={this.props.qaRuleCollection.sampleReason}
              guideline={this.props.qaRuleCollection.guideline}
            />
          ) : (
            <div
              className={`status-area conditional-view ${
                !hasStatusReason ? 'default-display-none-on-false' : ''
              }`}>
              <div className="status-section case-status-section">
                {benchLoading ? (
                  <LoadingSlugs key="loading-slugs" numberItems={1} />
                ) : (
                  <StatusDescription
                    secondaryPrefix="Review Required"
                    includeBox={true}
                    caseRun={caseDetailProps.activeItem}
                  />
                )}
              </div>
            </div>
          )}

          {/* Late case update statuses */}
          {benchLoading ? (
            <LoadingSlugs key="loading-slugs" numberItems={2} />
          ) : (
            <>
              <Conditional
                every={[!!this.props.processInstance?.areCodesCopied]}>
                <CaseAlert alertType={CaseAlertType.INFO}>
                  This case has been updated with a new version since submission
                  from your coder. We haven’t changed any of their work;
                  however, you may want to review the case and see if changes
                  are required because of the updated information.
                </CaseAlert>
              </Conditional>
              <Conditional
                every={[
                  ENABLE_REFERENCE_CODES_LEDGER_UPDATE_BANNER,
                  hasLedgerUpdates(this.props.processInstance)
                ]}>
                <div className="reference-update-banner">
                  <CaseAlert alertType={CaseAlertType.WARNING}>
                    <div className="reference-update-alert">
                      <div className="reference-update-alert-header">
                        <strong>New information available!</strong>
                      </div>

                      <div>
                        Reference codes for this case have changed. We suggest double checking
                        the codes in the Codes Source tab to see if you need to make changes
                        because of an updated source set or prediction set.
                      </div>
                    </div>
                  </CaseAlert>
                </div>
              </Conditional>
              <Conditional {...capabilities.capabilities.previousSentCaseAlert}>
                <CaseAlert alertType={CaseAlertType.ERROR}>
                  This is an update to a{' '}
                  <a
                    className="anchor-chakra-link"
                    onClick={() =>
                      handleOpenCase(previousSentCase?.processInstanceGuid)
                    }>
                    previously submitted case
                  </a>
                  .
                </CaseAlert>
              </Conditional>
              <Conditional {...capabilities.capabilities.oldCaseLoadedAlert}>
                <CaseAlert alertType={CaseAlertType.WARNING}>
                  You are viewing a non-codable version of this case. View the{' '}
                  <a
                    className="anchor-chakra-link"
                    onClick={() =>
                      handleOpenCase(newestVersionObject?.processInstanceGuid)
                    }>
                    codable version
                  </a>
                </CaseAlert>
              </Conditional>
              <Conditional
                some={[!!this.props.activeItem?.parent_process_instance_uid]}>
                <CaseAlert alertType={CaseAlertType.INFO} alertSize="sm">
                  <Conditional
                    every={[
                      !!this.props.activeItem?.parent_process_instance_uid
                    ]}>
                    <span>
                      This case is no longer active. It has been merged into:
                      case
                      <a
                        className="anchor-chakra-link"
                        onClick={() =>
                          handleOpenCase(
                            this.props.activeItem?.parent_process_instance_uid,
                            this.props.activeItem?.parent_case_uid
                          )
                        }>
                        {' '}
                        #{this.props.activeItem?.parent_case_id}
                      </a>
                    </span>
                  </Conditional>
                </CaseAlert>
              </Conditional>
            </>
          )}
          <div className="charge-sessions">{innerBench}</div>

          {UIMode === commonEnums.UIModesEnum.EPIC_EMBEDDED ? (
            <div className="debug-box">
              <a className="debug-link" href="/debug">
                Debug
              </a>
            </div>
          ) : (
            ''
          )}
        </>
      ];
    }

    const _updatedPropertyConfig = updatePropertyConfig({
      benchMode,
      serviceLine: this.props.activeItem.service_line,
      propertyConfig: this.props.propertyConfig
    });

    return (
      <div className={styles}>
        {prompt && (
          <div className="top-level-modal">
            <Modal
              title={prompt.text}
              closable={false}
              onSubmit={this.handlePromptDismiss}
              buttonText="Yes"
              maximize={false}>
              <p>
                Click <b>Yes</b> below to stay logged in.
              </p>
            </Modal>
          </div>
        )}
        <Confirm
          onConfirm={this.props.handleConfirm}
          onDismiss={this.props.handleConfirmDismiss}
          {...confirm}
        />
        {this.state.showCaseConflictModal && (
          <Modal
            title={`This case is currently open by ${this.state.conflictingUser}`}
            buttonText="I understand"
            closable={false}
            icon={<img src={dataURIs.AlertIcon} width={30} height={30} />}
            onSubmit={() => this.setState({ showCaseConflictModal: false })}>
            <p>
              Another user is editing this case at the moment. If you make
              edits, it could interfere with their work.
            </p>
          </Modal>
        )}

        {showModal && (
          <Modal onClose={this.handleModalClose} maximize={true}>
            <iframe
              ref={this.iframeRef}
              src={modalURI}
              className="popup-frame"
            />
          </Modal>
        )}
        {(showSubmittedFeedback || showCanceledFeedback) && (
          <div className="qa-submitted-modal">
            <UniversalFeedback
              feedback={{
                message: `Successfully ${
                  showSubmittedFeedback ? 'submitted' : 'canceled'
                }`,
                id: new Date().getTime(),
                dismissable: true
              }}
              showingTime={3000}
              dismissFeedback={this.props.hideSubmittedFeedback}
              showFeedback={showSubmittedFeedback || showCanceledFeedback}
            />
          </div>
        )}
        {(showLoadingSubmission || showLoadingCancelation) && (
          <div className="qa-submitted-modal">
            <UniversalFeedback
              feedback={{
                message: `${
                  showLoadingSubmission ? 'Submitting...' : 'Canceling...'
                }`,
                id: new Date().getTime(),
                dismissable: false,
                meta: { level: commonEnums.FeedbackMeta.WARNING }
              }}
              showFeedback={showLoadingSubmission || showLoadingCancelation}
            />
          </div>
        )}
        {displayAddCharge && (
          <Modal
            onClose={this.onModalAddChargeClose}
            title={
              showAddChargeLoading ? 'Adding charge' : 'Add a charge for:'
            }>
            <div className="qa-add-charge-modal">
              {!showAddChargeLoading ? (
                <ChargeSelector
                  handleAddCharge={this.handleAddCharge}
                  handleInvoiceAddition={this.handleInvoiceAddition}
                  invoiceOptions={
                    invoiceOptions as any as AmplifyAPI.InvoiceOptionsDTO
                  }
                />
              ) : (
                <div className="spinner-box">
                  <img src={SpinnerTag} alt="in progress" className="spinner" />
                </div>
              )}

              <div className="charge-error-box">
                {showAddChargeError ? (
                  <small className="charge-error-text">
                    Error adding a new charge
                  </small>
                ) : (
                  ''
                )}
              </div>
            </div>
          </Modal>
        )}

        {/*CQA-UI: call casedetailv2 */}
        <CaseDetailv2
          ref={this.caseDetailRef}
          mode="code-edit"
          codeEdits={codeEdits}
          codeEditingPanel={codeEditingPanel}
          groupedCases={groupedCases}
          {...{
            ...caseDetailProps,
            propertyConfig:
              benchMode === commonEnums.BenchMode.GRADE
                ? _updatedPropertyConfig
                : caseDetailProps.propertyConfig,
            demographicsPanelLocation: caseDetailProps.demographicsPanelLocation,
            demographicsPanelVisibility: caseDetailProps.demographicsPanelVisibility
          }}
          loadState={loadState}
          activations={activations}
          actionBar={actionBar}
          updateValue={updateValue}
          updateCase={updateCase}
          showAttachDocsModal={this.props.showAttachDocsModal}
          capability={capabilities.capabilities.caseEdit}
          coderNotesCapability={capabilities.capabilities.coderNotes}
          capabilities={capabilities}
          processInstance={this.props.processInstance}
          caseEdits={caseEdits}
          noteUnavailableReason={
            capabilities.capabilities.noteUnavailableReason
          }
          caseAccessState={this.props.caseAccessState}
          UIMode={this.props.UIMode}
          flagList={flagList}
          relatedCasesView={relatedCasesView}
          activity={activity}
          documentImprovementsPanel={
            <Improvements
              {...this.props}
              improvementsAction={capabilities.capabilities.improvementsAction}
              showUnresolved={this.props.showUnresolvedDocuments}
            />
          }
          showCoderNotesInfo={this.props.showCoderNotesInfo}
          workItem={workItem}
          displayTopLeftPatientInfo={
            !(this.props.benchMode == commonEnums.BenchMode.GRADE)
          }
          editable={benchEditable.editable}
          enableUserActivityComments={true}
        />
        <UniversalFeedback
          feedback={feedback}
          dismissFeedback={this.props.hideFeedback}
          showFeedback={showFeedBack}
          showingTime={5000}
        />
        {showSizeExpWarning && (
          <div className="top-level-modal">
            <Modal
              title={'Help us improve your experience'}
              closable={true}
              onClose={this.handleDeleteSizeExpWarning}
              onSubmit={this.handleDeleteSizeExpWarning}
              buttonText="Dismiss"
              maximize={false}>
              <p>
                Consider adjusting the width of your browser to a wider setting
                for a better view of CMX Amplify.
              </p>
            </Modal>
          </div>
        )}
        {showvitalwareIssuesModal && (
          <VitalWareModal
            onClose={this.props.hidevitalwareIssuesModal}
            onSubmit={(event, comment, remove_hold) =>
              this.handleSubmit(event, comment, true, remove_hold)
            }
            vitalwareIssues={vitalwareIssues}
            mergeSplitHistoryIssues={mergeSplitHistoryIssues}
            documentImprovementIssues={documentImprovementIssues}
            holdIssues={holdIssues}
            holdCaseStatus={capabilities.holdCaseStatus}
            improvements={this.props.improvements?.improvements}
            setActiveMiniTab={this.props.setActiveVerticalMiniTab}
            setUnresolvedDocuments={this.props.setUnresolvedDocuments}
          />
        )}
      </div>
    );
  }
}

export { CodeBenchv2 };
